import { apiBaseUrl } from '../config/apiConfig';

const openingHoursApiUrl = `${apiBaseUrl}/api/openinghours`;

// Utility function to get token and handle missing authentication
const getToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Utility function to handle API responses
const handleApiResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'API request failed');
  }
  return data;
};

// Fetch opening hours for a business
export const fetchOpeningHours = async (businessId, date) => {
  const response = await fetch(`${openingHoursApiUrl}/business/${businessId}/date?value=${date}`, {
    method: 'GET',
  });
  return await handleApiResponse(response);
};

// Fetch opening hours for a business in a period
export const fetchOpeningHoursForPeriod = async (businessId, fromDate, toDate) => {
  const url = `${openingHoursApiUrl}/business/${businessId}/period?from=${fromDate}&to=${toDate}`;

  const response = await fetch(url);

  const json = await response.json().catch(() => null);

  if (!response.ok) {
    const msg = json?.message || "Request failed";
    throw new Error(msg);
  }

  return json?.$values || [];
};

export const addOpeningHoursList = async (businessId, list) => {
  const url = `${openingHoursApiUrl}/business/${businessId}/add-list`;
  const token = getToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(list),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(json?.message || "Failed to add opening hours.");
  }

  return json;
};

export const deleteOpeningHourRange = async (id) => {
  const token = getToken();

  const response = await fetch(`${openingHoursApiUrl}/${id}/range`, {
    method: "DELETE",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const json = await response.json().catch(() => null);
    throw new Error(json?.message || "Failed to delete opening hour.");
  }

  return true;
};
export const getOpeningHoursAll = async (businessId) => {
  const token = getToken();
  const response = await fetch(`${openingHoursApiUrl}/business/${businessId}/all`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  const data = await handleApiResponse(response);
  return Array.isArray(data.$values) ? data.$values : [];
};

export const deleteOpeningHour = async (id) => {
  const token = getToken();
  const response = await fetch(`${openingHoursApiUrl}/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!response.ok) throw new Error("Failed to delete opening hour");
};

// Add new opening hours
export const addOpeningHours = async (businessId, openingHoursDetails) => {
  const token = getToken();
  const response = await fetch(`${openingHoursApiUrl}/business/${businessId}/add`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(openingHoursDetails)
  });
  return await handleApiResponse(response);
};

// Update existing opening hours
export const updateOpeningHours = async (businessId, openingHoursDetails) => {
  const token = getToken();
  const response = await fetch(`${openingHoursApiUrl}/business/${businessId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(openingHoursDetails)
  });
  return await handleApiResponse(response);
};

// Delete opening hours for a business
export const deleteOpeningHours = async (businessId) => {
  const token = getToken();
  const response = await fetch(`${openingHoursApiUrl}/business/${businessId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  return await handleApiResponse(response);
};